import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBuilding,
  FaChartColumn,
  FaClock,
  FaFileCircleCheck,
  FaFileLines,
  FaPlaneDeparture,
  FaTicket,
  FaXmark,
  FaIdBadge,
  FaLayerGroup,
  FaListCheck,
  FaRegFileLines,
  FaScaleBalanced,
  FaFileInvoiceDollar,
} from "react-icons/fa6";
import { HiUsers } from "react-icons/hi2";
import {
  MdOutlineArrowDropDown,
  MdOutlineArrowDropUp,
  MdOutlineBusinessCenter,
  MdOutlineSummarize,
} from "react-icons/md";
import { RiBuilding2Line } from "react-icons/ri";
import { PiChartLineUpBold } from "react-icons/pi";
import { TbTargetArrow } from "react-icons/tb";
import ErrorModal from "./ErrorModel";
import { GetEmployeeModuleList } from "../../services/Permissions/PermissionsApi";

const menus = [
  {
    name: "Dashboard",
    moduleKey: "Dashboard",
    path: "/dashboard",
    icon: <FaChartColumn className="app-icon" />,
  },
  {
    name: "Admin Users",
    moduleKey: "AdminUser",
    path: "/admin-users",
    icon: <HiUsers className="app-icon" />,
    adminOnly: true,
  },
  {
    name: "Customer / Firm ",
    moduleKey: "Customer",
    path: "/customer-firm",
    icon: <FaBuilding className="app-icon" />,
  },
  {
    name: "Employee",
    moduleKey: "Employee",
    path: "/employees",
    icon: <HiUsers className="app-icon" />,
  },
  {
    name: "Deal and Quotation",
    icon: <PiChartLineUpBold className="app-icon" />,
    children: [
      { name: "Deal Management", moduleKey: "Deal", path: "/lead-management", icon: <TbTargetArrow className="app-icon text-sm" /> },
      { name: "Quotation", moduleKey: "Quotation", path: "/quotation", icon: <FaFileInvoiceDollar className="app-icon text-sm" /> },
    ],
  },
  {
    name: "Work Order",
    moduleKey: "WorkOrder",
    path: "/work-order",
    icon: <FaFileLines className="app-icon" />,
  },
  {
    name: "Tickets",
    moduleKey: "Tickets",
    path: "/tickets",
    icon: <FaTicket className="app-icon" />,
  },
  {
    name: "Leave",
    moduleKey: "Leave",
    path: "/leave",
    icon: <FaPlaneDeparture className="app-icon" />,
  },
  {
    name: "Todays Team Status",
    icon: <FaClock className="app-icon" />,
    children: [
      { name: "Office Team", moduleKey: "OfficeTeam", path: "/todays-team-status/office-team", icon: <RiBuilding2Line className="app-icon text-sm" /> },
      { name: "Field Team", moduleKey: "FieldTeam", path: "/todays-team-status/field-team", icon: <HiUsers className="app-icon text-sm" /> },
    ],
  },
  {
    name: "Employee Reports",
    moduleKey: "EmployeeReport",
    path: "/reports/weekly",
    icon: <FaRegFileLines className="app-icon" />,
  },
  {
    name: "Reports",
    icon: <MdOutlineSummarize className="app-icon" />,
    children: [
      { name: "Leave Report", moduleKey: "LeaveReport", path: "/reports/leave", icon: <FaFileCircleCheck className="app-icon text-sm" /> },
      { name: "Regularization Report", moduleKey: "RegularizationReport", path: "/reports/regularization", icon: <FaListCheck className="app-icon text-sm" /> },
    ],
  },
//   {
//     name: "Products",
//     path: "/products",
//     icon: <FaBoxOpen className="app-icon" />,
//   },

   {
    name: "Master",
    icon: <MdOutlineBusinessCenter className="app-icon" />,
    children: [
      { name: "Office", moduleKey: "Office", path: "/office", icon: <RiBuilding2Line className="app-icon text-sm" /> },
      { name: "Designation", moduleKey: "Designation", path: "/designation", icon: <FaIdBadge className="app-icon text-sm" /> },
      { name: "Quotation No. Format", moduleKey: "QuotationNoFormat", path: "/quotation-no-format", icon: <FaLayerGroup className="app-icon text-sm" /> },
      { name: "Table Header", moduleKey: "TableHeader", path: "/table-header", icon: <FaChartColumn className="app-icon text-sm" /> },
      { name: "Activity Type", moduleKey: "ActivityType", path: "/activity-type", icon: <FaListCheck className="app-icon text-sm" /> },
      { name: "Terms & Conditions", moduleKey: "TermsAndCondition", path: "/terms-and-conditions", icon: <FaScaleBalanced className="app-icon text-sm" /> },
    ],
  },
];

const getStoredLogin = () => {
  try {
    return JSON.parse(localStorage.getItem("login")) || {};
  } catch {
    return {};
  }
};

const normalizeModuleName = (value = "") =>
  String(value).replace(/\s+/g, "").toLowerCase();

const getVisibleMenus = (storedLogin) => {
  const login = storedLogin || getStoredLogin();
  const moduleList = Array.isArray(login.moduleList) ? login.moduleList : [];
  const assignedModules = moduleList.filter((item) => item?.isAssigned);

  const isAdmin = Number(login.roleTypeID || login.roleTypeId) === 1;
  const allowedForLogin = (menu) => !menu.adminOnly || isAdmin;

  if (moduleList.length === 0) {
    return menus.filter(allowedForLogin);
  }

  const assignedModuleNames = new Set(
    assignedModules.map((item) => normalizeModuleName(item.compareName)),
  );
  const getAssignedModule = (menu) =>
    assignedModules.find(
      (item) =>
        normalizeModuleName(item.compareName) ===
        normalizeModuleName(menu.moduleKey),
    );
  const canView = (menu) =>
    allowedForLogin(menu) &&
    (!menu.moduleKey ||
      assignedModuleNames.has(normalizeModuleName(menu.moduleKey)));
  const withModuleLabel = (menu) => {
    const permissionModule = getAssignedModule(menu);
    return permissionModule?.moduleName
      ? { ...menu, name: permissionModule.moduleName }
      : menu;
  };

  return menus
    .map((menu) => {
      if (!menu.children) {
        return canView(menu) ? withModuleLabel(menu) : null;
      }

      const children = menu.children
        .filter(canView)
        .map(withModuleLabel);
      return children.length > 0 ? { ...menu, children } : null;
    })
    .filter(Boolean);
};

const getLoginEmployeeKeyID = (login = {}) =>
  login.employeeKeyID ||
  login.employeeKeyId ||
  login.userKeyID ||
  login.userKeyId ||
  login.UserKeyID ||
  null;

const hasModuleAccess = (moduleList = [], moduleKey) => {
  if (!moduleKey || !Array.isArray(moduleList) || moduleList.length === 0) {
    return true;
  }

  return moduleList.some(
    (item) =>
      normalizeModuleName(item?.compareName) === normalizeModuleName(moduleKey) &&
      item?.isAssigned,
  );
};

const Sidebar = ({ isOpen, isMobile = false, onClose, setSelectedMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastSyncedPathRef = useRef("");
  const [openGroup, setOpenGroup] = useState("");
  const [visibleMenus, setVisibleMenus] = useState(getVisibleMenus);
  const [isRefreshingPermissions, setIsRefreshingPermissions] = useState(false);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const mobileSidebarWidth = "240px";

  const showPermissionError = () => {
    setErrorMessage("You have no permission to access this module.");
    setIsErrorOpen(true);
  };

  const refreshVisibleMenus = useCallback(async () => {
    const login = getStoredLogin();
    const employeeKeyID = getLoginEmployeeKeyID(login);

    if (!employeeKeyID) {
      const fallbackMenus = getVisibleMenus(login);
      setVisibleMenus(fallbackMenus);
      return {
        moduleList: Array.isArray(login.moduleList) ? login.moduleList : [],
        menus: fallbackMenus,
      };
    }

    setIsRefreshingPermissions(true);
    try {
      const response = await GetEmployeeModuleList(employeeKeyID);
      const moduleList = response?.statusCode === 200 && Array.isArray(response?.responseData?.data)
        ? response.responseData.data
        : Array.isArray(login.moduleList)
          ? login.moduleList
          : [];
      const updatedLogin = {
        ...login,
        moduleList,
      };

      localStorage.setItem("login", JSON.stringify(updatedLogin));

      const nextMenus = getVisibleMenus(updatedLogin);
      setVisibleMenus(nextMenus);

      return { moduleList, menus: nextMenus };
    } catch {
      const fallbackMenus = getVisibleMenus(login);
      setVisibleMenus(fallbackMenus);
      return {
        moduleList: Array.isArray(login.moduleList) ? login.moduleList : [],
        menus: fallbackMenus,
      };
    } finally {
      setIsRefreshingPermissions(false);
    }
  }, []);

  useEffect(() => {
    refreshVisibleMenus();
  }, [refreshVisibleMenus]);

  const handleNavigate = async (menu) => {
    if (!menu?.path || isRefreshingPermissions) return;

    const { moduleList } = await refreshVisibleMenus();

    if (!hasModuleAccess(moduleList, menu.moduleKey)) {
      showPermissionError();
      return;
    }

    navigate(menu.path);

    if (isMobile) {
      onClose?.();
    }
  };

  const handleGroupToggle = async (event, menu) => {
    event.preventDefault();
    event.stopPropagation();

    if (!menu?.children?.length) {
      showPermissionError();
      return;
    }

    setOpenGroup((current) =>
      current === menu.name ? "" : menu.name,
    );

    if (isRefreshingPermissions) return;

    const { menus: refreshedMenus } = await refreshVisibleMenus();
    const refreshedGroup = refreshedMenus.find((item) => item.name === menu.name);

    if (!refreshedGroup?.children?.length) {
      showPermissionError();
      return;
    }
  };

  const handleGroupKeyDown = (event, menu) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    handleGroupToggle(event, menu);
  };

  const isMenuActive = (menu) => {
    if (menu.children) {
      return menu.children.some(
        (child) =>
          location.pathname === child.path ||
          location.pathname.startsWith(`${child.path}/`),
      );
    }

    return location.pathname === menu.path || location.pathname.startsWith(`${menu.path}/`);
  };

  useEffect(() => {
    let selected = "";
    let activeGroup = "";

    visibleMenus.forEach((menu) => {
      if (menu.children) {
        const activeChild = menu.children.find(
          (child) =>
            location.pathname === child.path ||
            location.pathname.startsWith(`${child.path}/`),
        );
        if (activeChild) {
          selected = activeChild.name;
          activeGroup = menu.name;
        }
      } else if (location.pathname === menu.path || location.pathname.startsWith(`${menu.path}/`)) {
        selected = menu.name;
      }
    });

    if (lastSyncedPathRef.current !== location.pathname) {
      setOpenGroup(activeGroup);
      lastSyncedPathRef.current = location.pathname;
    }

    setSelectedMenu(selected);
  }, [location.pathname, setSelectedMenu, visibleMenus]);

  return (
    <>
      {isMobile && isOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={() => onClose?.()}
          className="fixed inset-0 z-30 bg-slate-950/40 backdrop-blur-[1px] md:hidden"
        />
      )}

      <div
        className={`app-sidebar fixed top-0 left-0 flex flex-col transition-all duration-300 ${
          isMobile
            ? `z-40 shadow-2xl md:hidden ${isOpen ? "translate-x-0" : "-translate-x-full"}`
            : "z-20"
        }`}
        style={{
          width: isMobile ? mobileSidebarWidth : isOpen ? "270px" : "78px",
          height: "100vh",
        }}
      >
        <div
          className={`flex h-[64px] items-center px-3 py-2.5 ${!isOpen && !isMobile ? "justify-center" : ""}`}
        >
          {isMobile ? (
            <div className="flex w-full items-center justify-between gap-3">
              <div className="sidebar-logo-shell is-open h-12 w-full max-w-[180px] flex-1">
                <div className="sidebar-logo-roll" aria-hidden="true" />
                <div className="sidebar-logo-panel">
                  <img
                    src="/images/elee-controls-logo.png"
                    alt="Elee Controls logo"
                    className="sidebar-logo-image max-h-8 w-auto"
                  />
                </div>
              </div>

              <button
                type="button"
                aria-label="Close sidebar"
                onClick={() => onClose?.()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-border bg-white text-text-primary shadow-sm transition hover:border-primary hover:text-primary"
              >
                <FaXmark className="text-xl" />
              </button>
            </div>
          ) : (
            <div
              className={`sidebar-logo-shell ${isOpen ? "is-open" : "is-collapsed"} ${isOpen ? "h-14 w-full max-w-[220px]" : "h-10 w-10"}`}
            >
              <div className="sidebar-logo-roll" aria-hidden="true" />
              <div className="sidebar-logo-panel">
                <img
                  src="/images/elee-controls-logo.png"
                  alt="Elee Controls logo"
                  className={`sidebar-logo-image ${isOpen ? "max-h-10" : "max-h-6"} w-auto`}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mx-3 mb-2.5 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        <div className="sidebar-scroll flex-1 overflow-y-auto px-2.5">
          {visibleMenus.map((menu) => {
            const active = isMenuActive(menu);

            if (menu.children) {
              return (
                <div key={menu.name} className="mb-2">
                  <button
                    type="button"
                    onPointerDown={(event) => handleGroupToggle(event, menu)}
                    onKeyDown={(event) => handleGroupKeyDown(event, menu)}
                    aria-expanded={openGroup === menu.name}
                    className={`mx-auto mb-1.5 flex w-[94%] items-center rounded-xl transition-all
                    ${isOpen || isMobile ? "gap-2 px-2.5 py-2" : "justify-center py-2"}
                    ${
                        active
                        ? "bg-primary text-white shadow-sm shadow-blue-100"
                        : "text-text-primary hover:bg-white hover:text-primary"
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20 text-[15px]">
                      {menu.icon}
                    </div>

                    {(isOpen || isMobile) && (
                      <>
                        <span className="flex-1 truncate text-left text-[13px] font-semibold leading-5">
                          {menu.name}
                        </span>
                        {openGroup === menu.name ? (
                          <MdOutlineArrowDropUp className="shrink-0 text-xl" />
                        ) : (
                          <MdOutlineArrowDropDown className="shrink-0 text-xl" />
                        )}
                      </>
                    )}
                  </button>

                  {(isOpen || isMobile) && openGroup === menu.name && (
                    <div className="ml-4 space-y-1.5 border-l border-border pl-3">
                      {menu.children.map((child) => (
                        <button
                          key={child.name}
                          type="button"
                          onClick={() => handleNavigate(child)}
                          disabled={isRefreshingPermissions}
                          className={`flex min-h-9 w-[94%] items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] font-medium leading-5 transition ${
                            location.pathname === child.path
                              ? "bg-white text-primary shadow-sm"
                              : "text-text-secondary hover:bg-white hover:text-primary"
                          }`}
                        >
                          {child.icon && (
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-muted text-[13px]">
                              {child.icon}
                            </span>
                          )}
                          <span className="truncate">{child.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={menu.name}>
                <button
                  type="button"
                  onClick={() => handleNavigate(menu)}
                  disabled={isRefreshingPermissions}
                  className={`mx-auto mb-1.5 flex w-[94%] items-center rounded-xl transition-all
                ${isOpen || isMobile ? "gap-2 px-2.5 py-2" : "justify-center py-2"}
                ${
                    active
                    ? "bg-primary text-white shadow-sm shadow-blue-100"
                    : "text-text-primary hover:bg-white hover:text-primary"
                }`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20 text-[15px]">
                    {menu.icon}
                  </div>

                  {(isOpen || isMobile) && (
                    <span className="flex-1 truncate text-left text-[13px] font-semibold leading-5">
                      {menu.name}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <ErrorModal
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default Sidebar;
